"use client";

import { formatImageUrl, VehicleMedia } from "@alba-cars/common-modules";
import { useEffect, useState } from "react";

import OptimizedImage from "@/components/common/images/OptimizedImage";
import Slider from "@/components/common/slider/Slider";
import IconArrowRight from "@/components/Icons/IconArrowRight";
import Modal from "@/components/ui/Modal";

interface GalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: VehicleMedia[];
  initialSlide: number;
  altText: string;
  imageObjectFit?: "cover" | "contain";
}

const GalleryModal: React.FC<GalleryModalProps> = ({
  isOpen,
  onClose,
  images,
  initialSlide,
  altText,
  imageObjectFit = "cover",
}) => {
  const [isMobileView, setIsMobileView] = useState(false);

  // Check for mobile screens on mount and window resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    // Initial check
    checkScreenSize();

    // Add resize listener
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      key={isOpen ? "open" : "closed"}
      className="!bg-black/50 !p-0"
      closeButtonClassName="!text-black !bg-white absolute right-4 top-4 cursor-pointer hover:opacity-90
        hover:!text-primary md:h-14 md:w-14 h-10 w-10 flex items-center justify-center rounded-full z-20"
      childrenClassName="max-h-screen"
    >
      <div className="relative mx-auto max-w-screen-2xl">
        <Slider
          initialSlide={initialSlide}
          paginationEnabled={false}
          slidesPerView={1}
          navigationEnabled={true}
          navigation={{
            nextEl: ".custom-swiper-button-next",
            prevEl: ".custom-swiper-button-prev",
          }}
          className={isMobileView ? "flex h-screen items-center" : ""}
          loop={true}
        >
          {images.map((image) => (
            <div
              className="relative flex w-full items-center justify-center"
              key={image.id}
              style={{
                height: isMobileView ? "300px" : "100vh",
                position: isMobileView ? "absolute" : "relative",
                top: isMobileView ? "50%" : "auto",
                transform: isMobileView ? "translateY(-50%)" : "none",
              }}
            >
              <OptimizedImage
                src={formatImageUrl(image.mediaUrl) ?? "/assets/images/tesla.jfif"}
                alt={altText}
                fill
                className={`overflow-hidden ${imageObjectFit === "contain" ? "object-contain" : "object-cover"}`}
                objectFit={imageObjectFit}
                imageLayout="full"
                loading="lazy"
              />
            </div>
          ))}
        </Slider>
        <div className="custom-swiper-button-prev absolute left-2 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white opacity-50 md:h-16 md:w-16 lg:flex lg:opacity-100">
          <button aria-label="Show previous image">
            <IconArrowRight
              fill="black"
              width={isMobileView ? "15" : "25"}
              height={isMobileView ? "15" : "25"}
              className="-rotate-180"
            />
          </button>
        </div>

        <div className="custom-swiper-button-next absolute right-2 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white opacity-50 md:h-16 md:w-16 lg:flex lg:opacity-100">
          <button aria-label="Show next image">
            <IconArrowRight fill="black" width={isMobileView ? "15" : "25"} height={isMobileView ? "15" : "25"} />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default GalleryModal;
